import type { ComponentProps, ReactNode } from 'react';
import { Document, Page } from 'react-pdf';
import { Rnd } from 'react-rnd';
import { ChevronLeft, ChevronRight, Minus, Plus } from 'lucide-react';
import { fieldBoxStyle } from '@tes/domain';
import { cn } from '@tes/ui';
import type { FieldKind, InputType, PageSize } from '../lib/types';

/**
 * The one colour scheme for placed fields — the editor canvas and the field
 * previewer draw from the same map so a signature box looks the same in both.
 * Keyed by kind for variable/static and by input type for signer fields.
 */
export const FIELD_COLORS: Record<Exclude<FieldKind, 'input'> | InputType, string> = {
  variable: 'border-brand-500 bg-brand-500/10 text-brand-700',
  // Slate: static text is the one kind nothing fills in, so it reads as part of
  // the page rather than as something awaiting a value.
  static: 'border-slate-400 bg-slate-400/10 text-slate-700',
  signature: 'border-violet-500 bg-violet-500/10 text-violet-700',
  initials: 'border-fuchsia-500 bg-fuchsia-500/10 text-fuchsia-700',
  date: 'border-amber-500 bg-amber-500/10 text-amber-700',
  text: 'border-sky-500 bg-sky-500/10 text-sky-700',
  checkbox: 'border-emerald-500 bg-emerald-500/10 text-emerald-700',
};

export function fieldColor(f: { kind: FieldKind; type?: InputType }): string {
  if (f.kind === 'variable' || f.kind === 'static') return FIELD_COLORS[f.kind];
  return FIELD_COLORS[f.type ?? 'text'];
}

export const ZOOM_MIN = 0.4;
export const ZOOM_MAX = 2.5;
export const ZOOM_STEP = 0.1;
export const zoomOut = (z: number) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2));
export const zoomIn = (z: number) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2));

/** A page whose size is not yet known renders at A4 until react-pdf reports it. */
const FALLBACK_SIZE: PageSize = { width: 595, height: 842 };

type DocumentFile = ComponentProps<typeof Document>['file'];

export interface PageCanvasProps {
  /** react-pdf's `file` prop — memoise it, or the document reloads every render. */
  file: DocumentFile;
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  /** Size of the current page in PDF points; undefined until known. */
  size: PageSize | undefined;
  onDocumentLoad?: (numPages: number) => void;
  /** Reported per page from react-pdf's pre-rotation dimensions. */
  onPageLoad?: (page: number, size: PageSize) => void;
  /** Clicking the empty page. */
  onDeselect?: () => void;
  /** Extra toolbar controls, after the zoom buttons. */
  toolbarExtra?: ReactNode;
  className?: string;
  /** The field overlay — `FieldBox`es positioned over the page. */
  children?: ReactNode;
}

/**
 * A PDF page with a pager/zoom toolbar and an overlay for draggable field
 * boxes. Owns nothing: the page, zoom and field state live with the caller
 * (the editor's store, or the previewer's local state).
 */
export function PageCanvas({
  file,
  page,
  pageCount,
  onPageChange,
  zoom,
  onZoomChange,
  size,
  onDocumentLoad,
  onPageLoad,
  onDeselect,
  toolbarExtra,
  className,
  children,
}: PageCanvasProps) {
  const known = size ?? FALLBACK_SIZE;
  const renderW = Math.round(known.width * zoom);
  const renderH = Math.round(known.height * zoom);

  return (
    <div className={cn('flex h-full flex-col', className)}>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-4 py-2 text-sm">
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="rounded p-1 hover:bg-slate-100 disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="tabular-nums text-slate-600">
            Page {page} / {pageCount || '…'}
          </span>
          <button
            type="button"
            className="rounded p-1 hover:bg-slate-100 disabled:opacity-40"
            disabled={page >= pageCount}
            onClick={() => onPageChange(page + 1)}
            aria-label="Next page"
          >
            <ChevronRight size={18} />
          </button>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="rounded p-1 hover:bg-slate-100"
            onClick={() => onZoomChange(zoomOut(zoom))}
            aria-label="Zoom out"
          >
            <Minus size={16} />
          </button>
          <span className="w-12 text-center tabular-nums text-slate-600">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            className="rounded p-1 hover:bg-slate-100"
            onClick={() => onZoomChange(zoomIn(zoom))}
            aria-label="Zoom in"
          >
            <Plus size={16} />
          </button>
        </div>
        {toolbarExtra}
        {size && (
          <span className="ml-auto text-xs text-slate-400">
            page {Math.round(size.width)} × {Math.round(size.height)} pt
          </span>
        )}
      </div>

      <div className="flex-1 overflow-auto bg-slate-100 p-8">
        <div
          className="relative mx-auto shadow-lg ring-1 ring-slate-300"
          style={{ width: renderW, height: renderH }}
          onMouseDown={onDeselect}
        >
          <Document
            file={file}
            onLoadSuccess={({ numPages }) => onDocumentLoad?.(numPages)}
            loading={<div className="p-8 text-sm text-slate-400">Loading document…</div>}
            error={<div className="p-8 text-sm text-red-500">Failed to load the document.</div>}
          >
            <Page
              pageNumber={page}
              // Once the point size is known, render to the exact pixel width
              // the overlay is laid out against; before that, scale the PDF's
              // own size so the first paint isn't at a guessed width.
              {...(size ? { width: renderW } : { scale: zoom })}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              onLoadSuccess={(p) =>
                onPageLoad?.(page, { width: p.originalWidth, height: p.originalHeight })
              }
            />
          </Document>
          {size && <div className="absolute inset-0">{children}</div>}
        </div>
      </div>
    </div>
  );
}

export interface FieldGeometry {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * One draggable, resizable field box over the page. Geometry is in PDF points
 * (top-left origin); `zoom` converts to CSS px with the same rule the signing
 * page uses, so the box the author drags is the box the signer sees.
 */
export function FieldBox({
  field,
  zoom,
  selected,
  onSelect,
  onChange,
  children,
}: {
  field: FieldGeometry & { kind: FieldKind; type?: InputType };
  zoom: number;
  selected: boolean;
  onSelect: () => void;
  onChange: (geometry: Partial<FieldGeometry>) => void;
  /** The preview content, filling the exact field rect. */
  children: ReactNode;
}) {
  const box = fieldBoxStyle(field, zoom);
  return (
    <Rnd
      bounds="parent"
      size={{ width: box.width, height: box.height }}
      position={{ x: box.left, y: box.top }}
      onMouseDown={(e: MouseEvent) => {
        e.stopPropagation();
        onSelect();
      }}
      onDragStop={(_e, d) => onChange({ x: d.x / zoom, y: d.y / zoom })}
      onResizeStop={(_e, _dir, ref, _delta, pos) =>
        onChange({
          w: ref.offsetWidth / zoom,
          h: ref.offsetHeight / zoom,
          x: pos.x / zoom,
          y: pos.y / zoom,
        })
      }
      className={cn(
        'overflow-hidden rounded',
        fieldColor(field),
        selected && 'ring-2 ring-slate-800 ring-offset-1',
      )}
    >
      {/* Outline drawn as floating chrome (not a layout border) so the preview
          content fills the EXACT field rect — a real border would inset it 2px
          and break preview↔stamp parity. */}
      <div className="pointer-events-none absolute inset-0 rounded border-2 border-inherit" />
      {children}
    </Rnd>
  );
}
